import type { ReferenceWordMorphemeSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { PinyinPronunciation } from "@-/dev.improve.haniverse.routes.nav.app._components.pinyin-pronunciation";
import {
  MorphemeInfoPanelDictionaryGradeBadge,
  useMorphemeInfoPanelDictionaryGradeLabel,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content.views.dictionary.__sections.overview._sections.grade-badge";
import { createWordGlyph } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.word.glyph.font";
import { GraphemeFontGlyph } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.grapheme.glyph.font";
import { useBrowseMorphemePageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.browse.morpheme.navigator";
import { useGetGraphemeGlyph } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.glyph/client";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import { useCallback, useMemo, useState } from "react";
import type { LayoutChangeEvent } from "@-/com.npmjs.react-native";
import { LongPressable } from "@-/dev.improve.haniverse.components.ui.long-pressable";
import { View } from "@-/com.npmjs.react-native";
import { morphemeSelectionItem } from "@-/dev.improve.haniverse.routes.nav.app._context";
import { useAppSelection } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.tile.selection";
import runes from "@-/com.npmjs.runes2";
import arrayLength from "@-/com.npmjs.extra-array__length";

/** Outer container horizontal padding (matches `p-3`). */
const WORD_SECTION_HORIZONTAL_PADDING = 12;
const WORD_GLYPH_MIN_FONT_SIZE = 16;
const WORD_GLYPH_LINE_HEIGHT_RATIO = 1.35;
/** Horizontal inset so glyph ink stays inside the word row. */
const WORD_GLYPH_HORIZONTAL_INSET = 10;
/** Initial width until the full-width word row is measured. */
const WORD_SECTION_FALLBACK_WIDTH = 320;

/**
 * Full-width word hero with plain (uncolored) glyphs, its HSK / TBCL grade badge above (when it
 * has one) and its tone-colored reading underneath. Each glyph links to its own character's info
 * page.
 */
export function MorphemeInfoPanelDictionaryWordSection({
  pinyinList,
  wordCard,
}: {
  /** Display strings (tone-marked) for the word's primary reading. */
  pinyinList: readonly string[];
  wordCard: ReferenceWordMorphemeSlice;
}) {
  const [glyphBounds, setGlyphBounds] = useState<{ height: number; width: number } | null>(null);
  const getGraphemeGlyph = useGetGraphemeGlyph();
  const { hanziScript } = useActorUser();
  const selectionItems = useMemo(() => [morphemeSelectionItem(wordCard.word)], [wordCard.word]);
  const { consumePress, isSelected, onLongPress } = useAppSelection(selectionItems);
  const navigateToMorpheme = useBrowseMorphemePageNavigator().push.mutate;
  const gradeLabel = useMorphemeInfoPanelDictionaryGradeLabel(wordCard);

  const characters = useMemo(() => runes(wordCard.word), [wordCard.word]);
  const characterCount = arrayLength(characters);

  // `PinyinPronunciation` plays a card's readings from `pinyinList`; a word slice keeps its reading
  // on the first definition instead, so adapt it to the shape the shared component expects.
  const audioCard = useMemo(() => {
    const primaryToneNumbersPinyin = wordCard.definitionList[0]?.pinyin;
    return {
      front: wordCard.word,
      pinyinList:
        primaryToneNumbersPinyin === undefined
          ? []
          : [{ toneNumbersPinyin: primaryToneNumbersPinyin }],
    };
  }, [wordCard.definitionList, wordCard.word]);

  // Word-page glyphs stay plain foreground; tone color lives on the pinyin row underneath so the
  // two layers don't compete.
  const wordGlyphList = useMemo(
    () =>
      createWordGlyph(
        characters.map((character) => getGraphemeGlyph(character, { uncolored: true })),
      ),
    [characters, getGraphemeGlyph],
  );

  const widthPx =
    glyphBounds?.width ?? WORD_SECTION_FALLBACK_WIDTH - WORD_SECTION_HORIZONTAL_PADDING * 2;
  const heightPx = glyphBounds?.height ?? widthPx;
  const paddedWidth = Math.max(0, widthPx - WORD_GLYPH_HORIZONTAL_INSET * 2);
  // A word is as many glyph boxes wide as it has characters, so the per-character size is bounded
  // by the row width DIVIDED by the character count — otherwise a 3-character word overflows.
  const boundedSize = Math.min(paddedWidth / Math.max(1, characterCount), heightPx, 80);
  const glyphFontSize = Math.max(
    WORD_GLYPH_MIN_FONT_SIZE,
    Math.floor(boundedSize / WORD_GLYPH_LINE_HEIGHT_RATIO),
  );
  // `text-base` bakes in a fixed 24px line height that survives the `fontSize` override and clips
  // the enlarged glyph — pin a proportional line height instead (same fix as the grapheme column).
  const glyphLineHeight = Math.round(glyphFontSize * WORD_GLYPH_LINE_HEIGHT_RATIO);

  const handleGlyphAreaLayout = useCallback((event: LayoutChangeEvent) => {
    const { height, width } = event.nativeEvent.layout;
    setGlyphBounds({ height, width });
  }, []);

  return (
    <View className="w-full shrink-0 flex-col gap-2 p-3">
      {gradeLabel !== null && (
        <View className="shrink-0 items-center">
          <MorphemeInfoPanelDictionaryGradeBadge label={gradeLabel} />
        </View>
      )}

      <View
        accessibilityLabel={`${wordCard.word}, dictionary word`}
        accessibilityState={{ selected: isSelected }}
        className={
          isSelected
            ? "h-20 w-full items-center justify-center overflow-hidden rounded-xs bg-primary/10 px-2.5"
            : "h-20 w-full items-center justify-center overflow-hidden px-2.5"
        }
        onLayout={handleGlyphAreaLayout}
      >
        <View className="max-w-full flex-row items-baseline justify-center">
          {wordGlyphList.map((glyph, index) => (
            <LongPressable
              accessibilityHint="Opens this character's info page"
              accessibilityLabel={glyph.original}
              accessibilityRole="link"
              className="rounded-xs active:bg-accent"
              key={`${glyph.original}-${index}`}
              // Long-pressing any character still selects the whole word.
              onLongPress={onLongPress}
              onPress={() => {
                if (consumePress()) {
                  return;
                }
                navigateToMorpheme({ morpheme: glyph.original, params: {} });
              }}
            >
              <GraphemeFontGlyph
                className="text-center text-foreground"
                glyph={glyph}
                hanziScript={hanziScript}
                style={{ fontSize: glyphFontSize, lineHeight: glyphLineHeight }}
              />
            </LongPressable>
          ))}
        </View>
      </View>

      <View className="shrink-0 items-center">
        <PinyinPronunciation card={audioCard} pinyinList={pinyinList} />
      </View>
    </View>
  );
}
